import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  LMListComponent,
  LMListTransactionEvent,
  LMPanelTransactionEvent,
  LMRefreshRequest
} from '@app/components';
import { LMFormatUtilService, LMUrlInfoBuilderService } from '@app/services';
import { LMConditionUtilService, LMMultiListSaveWorker } from '@app/utils';
import { forkJoin, Observable, Observer } from 'rxjs';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[lm-page-pane-multi-list]',
  templateUrl: 'lm-page-pane-multi-list.component.html',
  styleUrls:   [ 'lm-page-pane-multi-list.component.scss' ]
})
export class LMPagePaneMultiListComponent {
  @ViewChildren('LMListComponent') listComponents: QueryList<LMListComponent>;

  @Input() panel: LMPagePanelSpec;

  @Input() set fromDataView(fromDataView: LMDataView) {
    this._fromDataView = fromDataView;
    this._numberVisibleLists = 0;
    this.listViewData = this.panel.panes.map(list => this.toListViewData(list));
  }

  get fromDataView(): LMDataView {
    return this._fromDataView;
  }

  @Output() contentModified = new EventEmitter<any>();
  @Output() formPanelContentLoaded = new EventEmitter<any>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() specialActionToolbarButtonEvent = new EventEmitter<LMSpecialActionToolbarButtonEvent>();
  @Output() panelTransactionEvent = new EventEmitter<LMPanelTransactionEvent>();

  private _fromDataView: LMDataView;
  public listViewData: Array<LMListViewData>;

  private _numberVisibleLists = 0;

  constructor(
    private lmFormatUtilService: LMFormatUtilService,
    private lmConditionUtilService: LMConditionUtilService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private elementRef: ElementRef
  ) { }

  public getPanelHeight(): number {
    if (this.elementRef) {
      return this.elementRef.nativeElement.offsetHeight;
    }
    return 0;
  }

  public get numberVisibleLists(): number {
    return this._numberVisibleLists;
  }

  private toListViewData(section: LMMultiListSectionSpec): LMListViewData {
    if (!section?.hasOwnProperty('listSpec')) {
      return null;
    }

    const listViewData: LMListViewData = {
      listUrlInfo: this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(section.listSpec),
      listSpec: section.listSpec
    };

    if (section.panelText) {
      listViewData.panelText = section.panelText;
    }

    if (section.showLines) {
      listViewData.listSpec.busListSpec.showLines = section.showLines;
    }

    listViewData.isVisible = (section.visibility)
      ? this.lmConditionUtilService.isValid([ section.visibility ], this._fromDataView)
      : true;

    if (listViewData.isVisible) {
      this._numberVisibleLists++;
    }

    if (section.specialActions) {
      listViewData.specialActions = section.specialActions;
    }

    return listViewData;
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onListDisplayed(event: any) {
    this.formPanelContentLoaded.emit(event);
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onSpecialActionToolbarButtonEvent(event: LMSpecialActionToolbarButtonEvent) {
    this.specialActionToolbarButtonEvent.emit(event);
  }

  onPanelTransactionEvent(event: LMListTransactionEvent) {
    const originatingId = this.panel.id;
    const originatingList = event.eventSource.listId;
    const panelTransactionEvt = new LMPanelTransactionEvent(originatingId);
    panelTransactionEvt.originatingList = originatingList;
    this.panelTransactionEvent.emit(panelTransactionEvt);
  }

  public refresh(request?: LMRefreshRequest): Observable<boolean> {
    const lists = this.listComponents ? this.listComponents.toArray() : [];
    return new Observable<boolean>(observer => {
      const refreshCalls: Observable<boolean>[] = lists.filter(list => list.listId !== request?.originatingSourceId)
        .map((list: LMListComponent) => list.refresh());

      // console.log('multilist to be refresh:' + refreshCalls.length);

      forkJoin(refreshCalls).subscribe(value => {
        // console.log('multilist to be forkJoin:');
        observer.next(true);
        observer.complete();
      });
    });
  }

  public save(callBack: Observer<boolean>) {
    this.runSaveList().subscribe(
      (continueOn: boolean) => {
        callBack.next(continueOn);
        callBack.complete();
      });
  }

  trackByListSpec(index: number, list: LMListViewData): LMListSpec {
    return list.listSpec;
  }

  private runSaveList(): Observable<boolean> {
    const listComponents: LMListComponent[] = this.listComponents.toArray();
    const saveWorker: LMMultiListSaveWorker = new LMMultiListSaveWorker(listComponents);
    return saveWorker.start();
  }

  public isDirty(): boolean {
    const listComponents: LMListComponent[] = this.listComponents.toArray();

    for (let i = 0, l = listComponents.length; i < l; i++) {
      if (listComponents[ i ].isModified()) {
        return true;
      }
    }
    return false;
  }

  saveListComponentState(): void {
    this.listComponents.toArray().forEach(listComponent => listComponent.saveComponentState(true));
  }

  clearListComponentState(): void {
    this.listComponents.toArray().forEach(listComponent => listComponent.clearComponentState(true));
  }
}
